import { fromJS } from 'immutable';
import request from '../request';
import profile from '../../services/gma/profile';
import { types } from '../../utils/js/types';

import { GMI, STATE } from '../../constants';

export const { COMMUNICATION_PREFERENCES } = STATE.UI.PROFILE;

/**
 * @private
 * @returns {Object} {type: string, path: [], value: object}
 */
function clearCommunicationPreferences() {
  return {type: GMI.SET_GMI_STATE, path: COMMUNICATION_PREFERENCES.PATH, value: {}};
}

/**
 * @private
 * @returns {Object} {type: string, path: [], value: object}
 */
function clearCommunicationPreferencesProfile() {
  return {type: GMI.SET_GMI_STATE, path: COMMUNICATION_PREFERENCES.PROFILE, value: {}};
}

/**
 * @private
 * @returns {Object} {type: string, path: [], value: object}
 */
function clearCommunicationPreferencesSubscriptions() {
  return {type: GMI.SET_GMI_STATE, path: COMMUNICATION_PREFERENCES.SUBSCRIPTIONS, value: {}};
}

/**
 * @private
 * @returns {Object} {type: string, path: [], value: object}
 */
function clearCommunicationPreferencesConsents() {
  return {type: GMI.SET_GMI_STATE, path: COMMUNICATION_PREFERENCES.CONSENTS, value: {}};
}

/**
 * @private
 * @param {Object} object
 * @returns {Object} {type: string, path: [], value: object}
 */
function setCommunicationPreferences(value) {
  return {type: GMI.SET_GMI_STATE, path: COMMUNICATION_PREFERENCES.PATH, value};
}

/**
 * @private
 * @param {Object} object
 * @example
 * {
 *  loyalty_number: "NSN6VCN",
 *  additional_info: {
 *    update_profile_mode: 'UPDATE'
 *  },
 *  preference: {
 *    email_preference: {
 *      rental_receipts: false
 *    }
 *  }
 * }
 * @returns {Object} {type: string, path: [], value: object}
 */
function setCommunicationPreferencesProfile(value) {
  return {type: GMI.SET_GMI_STATE, path: COMMUNICATION_PREFERENCES.PROFILE, value};
}

/**
 * @private
 * @param {Object} object
 * @returns {Object} {type: string, path: [], value: object}
 */
function setCommunicationPreferencesSubscriptions(value) {
  return {
    type: GMI.SET_GMI_STATE, 
    path: COMMUNICATION_PREFERENCES.SUBSCRIPTIONS, 
    value
  };
}

/**
 * @private
 * @param {Object} object
 * @returns {Object} {type: string, path: [], value: object}
 */
function setCommunicationPreferencesConsents(value) {
  return {
    type: GMI.SET_GMI_STATE, 
    path: COMMUNICATION_PREFERENCES.CONSENTS,
    value
  };
}

/**
 * @private
 * @name setUiCommunicationPreferences
 * @param {Object} [opts] { subscriptionsState: Object }
 * @desc A setter for all profile payload data
 * @returns {Function}
 */
function setUiCommunicationPreferences(opts = {}) {
  return dispatch => dispatch(communicationPreferencesState.set(null, opts.subscriptionsState));
}

export const communicationPreferencesState = {
  /**
   * @name set
   * @desc A global setter for ui.profile.communication_preferences data
   * @param {String} [type]
   * @param {Object} [args]
   * @returns {Function}
   * @memberof actions.profile.communicationPreferences
   * @example
   * 
   * // Set CommunicationPreferencesProfile
   * store.dispatch(actions.profile.communicationPreferencesState.set('profile', {}));
   * 
   * // Set CommunicationPreferencesSubscriptions
   * store.dispatch(actions.profile.communicationPreferencesState.set('subscriptions', {}));
   * 
   * // Set CommunicationPreferencesConsents
   * store.dispatch(actions.profile.communicationPreferencesState.set('consents', {}));
   * 
   * // Set CommunicationPreferences
   * store.dispatch(actions.profile.communicationPreferencesState.set(null, {}));
   * 
   */
  set(type = null, args = {}) {
    return dispatch => {
      if (types(type).isString) {
        if (type === 'profile') {
          dispatch(setCommunicationPreferencesProfile(args));
        } else if (type === 'subscriptions') {
          dispatch(setCommunicationPreferencesSubscriptions(args));
        } else if (type === 'consents') {
          dispatch(setCommunicationPreferencesConsents(args));
        }
      } else {
        dispatch(setCommunicationPreferences(args));
      }
    };
  },
  
  /**
   * @name unset
   * @desc A global unsetter for profile.communicationPreferences data
   * @returns {Function}
   * @memberof actions.profile.communicationPreferences
   * @example
   * 
   * // Clear CommunicationPreferencesProfile
   * store.dispatch(actions.profile.communicationPreferencesState.unset('profile'));
   * 
   * // Clear CommunicationPreferencesSubscriptions
   * store.dispatch(actions.profile.communicationPreferencesState.unset('subscriptions'));
   * 
   * // Clear CommunicationPreferencesConsents
   * store.dispatch(actions.profile.communicationPreferencesState.unset('consents'));
   * 
   * // Clear CommunicationPreferences
   * store.dispatch(actions.profile.communicationPreferencesState.unset());
   * 
   */
  unset(type = null) {
    return dispatch => {
      if (types(type).isString) {
        if (type === 'profile') {
          dispatch(clearCommunicationPreferencesProfile());
        } else if (type === 'subscriptions') {
          dispatch(clearCommunicationPreferencesSubscriptions());
        } else if (type === 'consents') {
          dispatch(clearCommunicationPreferencesConsents());
        }
      } else {
        dispatch(clearCommunicationPreferences());
      }
    };
  }
};

/**
 * Auth update subscriptions
 * @param {Object} object
 * @returns {Object} {type: string, path: [], value: object}
 */
export function communicationPreferences() {
  return (dispatch, getState) => {
    const state = getState();

    const payload = state.getIn(COMMUNICATION_PREFERENCES.PATH).toJS();

    return dispatch(
      request({
        service: profile.communicationPreferences,
        data: { payload }
      })
    );
  };
}

/**
 * Unauth update subscriptions
 * @param {Object} object
 * @returns {Object} {type: string, path: [], value: object}
 */
export function updateSubscriptions() {
  return (dispatch, getState) => {
    const state = getState();

    const payload = state.getIn(COMMUNICATION_PREFERENCES.PATH).toJS();

    return dispatch(
      request({
        service: profile.updateSubscriptions,
        data: { payload }
      })
    );
  };
}

/**
 * @private
 * @desc gets data from communication_preferences to be saved to UI state
 * @param {Immutable} session
 * @returns {Object}
 */
function getCommunicationPreferencesState(session) {
  return session.getIn(['gma', 'communication_preferences'])?.toJS();
}

/**
 * @name hydrateUiProfileCommunicationPreferencesState
 * @desc hydrates gmi.ui source of truth state from session
 * @param {Object} [opts] {initiateFromGma:Boolean, actionSession:Object}
 * @returns {Function}
 * @memberof actions.profile
 * @example
 * dispatch(actions.profile.hydrateUiProfileCommunicationPreferencesState());
 * // will populate...
 * // state.gmi.ui.profile.subscriptions
 */
export function hydrateUiProfileCommunicationPreferencesState(opts = {}) {
  const { actionSession } = opts;
  return (dispatch, getState) => {
    const state = getState();
    const session =  fromJS(actionSession) || state.getIn(STATE.SESSION);
    const subscriptionsState = getCommunicationPreferencesState(session);

    if(subscriptionsState) return dispatch(setUiCommunicationPreferences({ subscriptionsState }));
  };
}